import React, { useContext, useEffect, useState } from "react";

import {
  InsertChart as InsertChartIcon,
  RemoveRedEye as RemoveRedEyeIcon,
} from "@material-ui/icons";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";

import Embedded from "./Embedded";
import MediaLink from "./MediaLink";
import EncoderAPI from "../../../api/EncoderAPI";
import ContentContext from "../../../contexts/ContentContext";
import useAbility from "../../../hooks/useAbility";
import HostedMedia from "../../../models/HostedMedia";
import { computeAnalyticPreviewAndEmbeddedLinks } from "../../../utils/data/Content";

import type { MediaChildren } from "../../../api/MediaAPI.types";
import type { AppState } from "../../../reducers";
import type { ContentContextValues } from "../../content/ContentTabs";

interface ChildContentHeaderProps {
  item: MediaChildren;
}

function ChildContentHeader({ item }: ChildContentHeaderProps) {
  const { canAccess } = useAbility();
  const { t } = useTranslation("editor-utils");
  const queryClient = useQueryClient();
  const { item: masterItem, languageId } =
    useContext<ContentContextValues>(ContentContext);
  const [previewDisabled, setPreviewDisabled] = useState(true);
  const orgaOptions = useSelector(
    (state: AppState) =>
      state.fetchReducer.orga_config.data?.organization_options,
  );
  const projects = useSelector(
    (state: AppState) => state.fetchReducer.projects,
  );
  const { contentsAnalytics: canAccessContentsAnalytics } =
    canAccess(orgaOptions);

  const Item = new HostedMedia({
    item,
    languageId,
    classification: item.child_classification,
  });

  const translatedSlug = Item.getTranslation("slug");

  const { previewLinks, analyticLinks, embeddedItems } =
    computeAnalyticPreviewAndEmbeddedLinks(
      {
        uuid: Item.uuid,
        translations: Item.translations,
        offers: masterItem.offers,
      },
      projects,
    );

  const getStreamsQuery = EncoderAPI.generateQuery({
    fn: EncoderAPI.getStreams,
    params: [Item.uuid],
  });

  useEffect(() => {
    if (Item.hasTemporaryId) return;

    const refetchStreamData = async () => {
      const { data: itemStreamData } =
        await queryClient.ensureQueryData(getStreamsQuery);

      const videoStreamTranscoded = itemStreamData?.some(
        (stream) =>
          ["video"].includes(stream.type) && stream.status === "transcoded",
      );

      setPreviewDisabled(
        masterItem.status !== "enabled" ||
          masterItem.publication_status !== "active" ||
          !translatedSlug ||
          !videoStreamTranscoded,
      );
    };

    refetchStreamData();
  }, [translatedSlug, masterItem]); // eslint-disable-line react-hooks/exhaustive-deps

  const displayAnalytics = canAccessContentsAnalytics && Boolean(analyticLinks);

  return (
    <div className="child-item-header section-separator">
      <h2 className="child-item-header__label">{Item.label}</h2>
      <div className="flex gap-[0.78125rem]">
        {Boolean(previewLinks) && (
          <MediaLink
            isExternal
            disabled={previewDisabled}
            icon={<RemoveRedEyeIcon />}
            label={t("viewLink")}
            link={previewLinks}
          />
        )}
        {displayAnalytics ? (
          <MediaLink
            icon={<InsertChartIcon />}
            label={t("analytics")}
            link={analyticLinks}
          />
        ) : null}
        {embeddedItems?.length > 0 && (
          <Embedded isVersion items={embeddedItems} />
        )}
      </div>
    </div>
  );
}

export default ChildContentHeader;
